// Rot & vermin symbols (32×32, id sym_<id>), docs/05 §1.3–1.4.
// Style matches symbols.ts: 1-px ink outline, light from top-left, readable by silhouette.
// Key: '.' transparent, 'o' ink outline, other chars per sprite.
// Rotten fruit is derived from the base fruit sprites: every color is remapped to a
// sickly/brown/olive palette key, then a fixed (deterministic) set of mold/rot spots is stamped.
import type { PaletteKey } from '../palette';
import { sprite, type SpriteDef } from './types';
import { cherry, lemon } from './symbols';
import { grape, melon, plum } from './symbols_classic';

export const maggot = sprite(
  'sym_maggot',
  { o: 'ink', c: 'cream', w: 'white', s: 'sand', m: 'mauveGrey', a: 'apricot', h: 'copper' },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '.........oooooooo...............',
    '......ooowwwwwwwwooo............',
    '.....owwwwcswwcswwwwoo..........',
    '....owcswccscccscwcswwoo........',
    '...owwcsccccscccsccswwwwoo......',
    '...owcccscccscccsccccccswwoo....',
    '..owhcccscccscccscccsccswsaao...',
    '..osccccscccscccscccsccmsssmo...',
    '..oshcccscccscccscccsssmsmmoo...',
    '..omssscscccscccscsmsssmmoo.....',
    '...omssmsssmsssmsssmsmmoo.......',
    '....ommmsssmsssmssmmmoo.........',
    '.....oommmmmmmmmmmooo...........',
    '.......ooooooooooo..............',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
  ],
);

export const fly = sprite(
  'sym_fly',
  {
    o: 'ink',
    n: 'slate',
    g: 'sage',
    G: 'clover',
    k: 'charcoal',
    r: 'rust',
    R: 'vermilion',
    b: 'maroon',
    w: 'steel',
    W: 'paper',
    l: 'lilacGrey',
  },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '...........ooo.oo.ooo...........',
    '..........oRRRoggoRRRo..........',
    '....o....oRrrrbooRrrrbo....o....',
    '...oko...oRrrrbooRrrrbo...oko...',
    '....oko..oRrrrbooRrrrbo..okko...',
    '....oko..obrrbboobrrbbo.okko....',
    '.....okoooobbbonkobbbooooko.....',
    '......okkkoooookkoooookkko......',
    '...o...oookognnoonnnokooo...o...',
    '..oko...oWoknnGnnnnkkowo...oko..',
    '...okoooWwwonGnnnnnkowwwoooko...',
    '....okkkkkkokknnnkkkokkkkkko....',
    '.....okkkkkkookkkkookkkkkko.....',
    '....oWwwwwwognoooonkowwwwwwo....',
    '...oWwwwwwwognGnnnnkowwwwwwlo...',
    '...oWwwwwwwogGnnnnnnowwwwwwwo...',
    '..oWwwwwwwognnnnnnnnkowwwwwwlo..',
    '..oWwwwwwwoknnnnnnnkkowwwwwwlo..',
    '..oWwwwwwllonnnnnnnkolwwwwwwwo..',
    '.oWwwwwwlloognnnnnnkoolwwwwwwlo.',
    '.olwwwwllo.oknnnnnkko.olwwwwllo.',
    '..olllllo...oknnnkko...olllllo..',
    '...ooooo.....okkkko.....ooooo...',
    '..............oooo..............',
    '................................',
    '................................',
    '................................',
    '................................',
  ],
);

export const mushroom = sprite(
  'sym_mushroom',
  {
    o: 'ink',
    m: 'mauveGrey',
    S: 'sand',
    P: 'plumGrey',
    d: 'dusk',
    k: 'khaki',
    p: 'paper',
    s: 'sand',
    c: 'cream',
    D: 'night',
  },
  [
    '................................',
    '............oooooooo............',
    '.........oooSSSSSSPPooo.........',
    '.......ooSSSmmmmmmmmmPPoo.......',
    '......oSSmmmmmmccmmmmmmPPo......',
    '.....oSmmccmmmmcSmmmmmmmPPo.....',
    '....oSmmmcSmmmmmmmmmmccmmPPo....',
    '...oSmmmmmmmmmmmmmmmmSmmmmPPo...',
    '...oSmmmmmmmcmmmmmcSmmmmmmPPo...',
    '..oSmmmmmmmmmmmmmmmmmmmmSmmPPo..',
    '..oSmmmmmmmmmmmmmmmmmmmmmmmPPo..',
    '..oPPPPPPPPPPPPPPPPPPPPPPPPPPo..',
    '..oPPPPPPPPPPPPPPPPPPPPPPPPPPo..',
    '...oooDdDdDdDdDdDdDdDdDdDdooo...',
    '......oddDdDdDdDdDdDdDdDdo......',
    '.......oooooooooooooooooo.......',
    '.........ospppppppppsso.........',
    '..........oskkkkkkksso..........',
    '...........osssssssso...........',
    '............oooooooo............',
    '...........opkkkkkkso...........',
    '...........opkkkkkkso...........',
    '...........opkkkkkkso...........',
    '...........opkkkkkkso...........',
    '..........opkkkkkkkkso..........',
    '..........opkkkkkkkkso..........',
    '..........osssssssssso..........',
    '..........osskkkkkssso..........',
    '...........oossssssoo...........',
    '.............oooooo.............',
    '................................',
    '................................',
  ],
);

export const rat = sprite(
  'sym_rat',
  { o: 'ink', l: 'lilacGrey', s: 'steel', d: 'dusk', q: 'salmonPink', Q: 'dustyPink', w: 'white' },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '...................ooo..........',
    '..................ossso.........',
    '.................osllllo........',
    '................osllqQldo.......',
    '................oslqqQldo.......',
    '...........oooooodlQQQddooo.....',
    '........ooossssssodllddollsoo...',
    '.......ossslllllllodddolllllso..',
    '.....ooslllllllllllooolllwolldo.',
    '....ossllllllllllldlllllloolllo.',
    '....osllllllllllllllllllllllldqo',
    '...oslllllllllllllldllllllllldQ.',
    '...oslllllllllllllllddllllllddo.',
    '...oslllllllllllllllddddlldddo..',
    '...osllllllllllllllldddodddoo...',
    '...odlllllllllllllddddo.ooo.....',
    '..oqdddlllllllllldddddo.........',
    '..oQQddddlllllddddddoo..........',
    '.oqQooodoooodddddddoqqo.........',
    '.oQQo..oqqqQodddoooQqQQo........',
    '.oQqooooQQQQoooo...oQQo.........',
    '..oQQQQQoooo........oo..........',
    '...ooooo........................',
    '................................',
    '................................',
  ],
);

export const cat = sprite(
  'sym_cat',
  {
    o: 'ink',
    n: 'night',
    u: 'dusk',
    U: 'lilacGrey',
    y: 'gold',
    Y: 'lemon',
    a: 'amber',
    p: 'dustyPink',
    P: 'mulberry',
  },
  [
    '........o.............o.........',
    '.......ouoo.........oono........',
    '.......ouPuo.......ouuPo........',
    '.......ouPPnooooooounPPo........',
    '.......ounnoouuuuuoonnno........',
    '.......ouoouunnnnnnuoono........',
    '........ouUnnnnnnnnnnuo.........',
    '.......ounnnnnnnnnnnnnno........',
    '.......ounnnnnnnnnnnnnno........',
    '......ounYyoynnnnnnYoyyno.......',
    '......ounyyoannnnnnyoyano.......',
    '......onnnnnnnnnnnnnnnnno.......',
    '.......onnnnnnnppnnnnnno........',
    '.......onnnnnnPPPPnnnnno........',
    '........onnnnnnnnnnnnno.oo......',
    '.........oonnnnnnnnnoo.ouuo.....',
    '...........oonnnnnoo...ounno....',
    '..........oouooooouoo..onnnno...',
    '.........ouunnnnnnnnuo..onnnno..',
    '........ounUnnnnnnnnnno..onnno..',
    '........ounnnnnnnnnnnno..ounno..',
    '.......ounnnnnnnnnnnnnno.ounnno.',
    '.......ounnnnnnnnnnnnnno.ounnno.',
    '.......ounnnnnnnnnnnnnno.ounno..',
    '.......ounnnnnnnnnnnnnnoounnno..',
    '.......onnnnnnnnnnnnnnnoounnno..',
    '........onnnnnnnnnnnnnoounnno...',
    '........onnnnnnnnnnnnnounnno....',
    '.........onnnnnnnnnnnounnno.....',
    '..........oonnnnnnnooonnno......',
    '............ooooooo...ono.......',
    '.......................o........',
  ],
);

export const worm = sprite(
  'sym_worm',
  { o: 'ink', s: 'salmonPink', a: 'apricot', d: 'dustyPink', r: 'dustyPink', R: 'rose' },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '...................oooooo.......',
    '..................osssaaao......',
    '.................oarrrsssso.....',
    '................oasrrrssssdo....',
    '...............oasssRRssssso....',
    '..............oasssddoddsssdo...',
    '..............oasssdo.oodssso...',
    '.............oasssddo...odssdo..',
    '...ooo......oasssddo.....odddo..',
    '..oaaao.....oasssdo.......odo...',
    '..oassso...oasssddo........o....',
    '..odsssso.oasssddo..............',
    '...osssssoassssdo...............',
    '...odsssssssssddo...............',
    '....odsssssssddo................',
    '.....odsssssddo.................',
    '......oddddddo..................',
    '.......oooooo...................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
  ],
);

export const spider = sprite(
  'sym_spider',
  { o: 'ink', n: 'night', u: 'dusk', l: 'lilacGrey', r: 'red', R: 'coral', b: 'blood' },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '.......ooo............ooo.......',
    '.....oouuuo..........ouuuoo.....',
    '....ouuooouo........ouooouuo....',
    '...ouoo..ouuo......ouuo..oouo...',
    '...ouo....ouuoooooouuo....ouo...',
    '..ouo......ououuuuouo......ouo..',
    '..ouo.ooooo.oulnnnno.ooooo.ouo..',
    '...o.ouuuuuounnrnrnnouuuuuo.o...',
    '....ouuooouounnnnnnnouooouuo....',
    '...ouuo...oonnnnnnnnoo...ouuo...',
    '..ouuo......onnnnnno......ouuo..',
    '..ouo...oo.ooonnnnooo.oo...ouo..',
    '...o...ouuouunoooonnnouuo...o...',
    '......ouooounnnnnnnnnooouo......',
    '.....ouuoounlnnnnnnnnnoouuo.....',
    '.....ouo.oulnnRrrrnnnno.ouo.....',
    '....ouo..ounnnnrbnnnnno..ouo....',
    '...ouuo..ounnnnrbnnnnno..ouuo...',
    '...ouo...ounnnrrbbnnnno...ouo...',
    '....o...oonnnnnnnnnnnnoo...o....',
    '.......ouoonnnnnnnnnnoouo.......',
    '.......ouoonnnnnnnnnnoouo.......',
    '......ouo..oonnnnnnoo..ouo......',
    '......ouo....oooooo....ouo......',
    '.......o................o.......',
    '................................',
    '................................',
    '................................',
  ],
);

export const crow = sprite(
  'sym_crow',
  {
    o: 'ink',
    n: 'night',
    u: 'indigo',
    b: 'cobalt',
    c: 'charcoal',
    D: 'dusk',
    d: 'dusk',
    w: 'white',
    l: 'sky',
  },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '........oooo....................',
    '.......ouuuuo...................',
    '......ounllnno..................',
    '.....ounnnnnnno.................',
    '...ooDcnwonnnnno................',
    '.ooDDccnoonnnnno................',
    'ocDccccnnnnnnnnoooo.............',
    '.occcccnnnnnnnnuuuuoo...........',
    '..oooocnnnnnnnoooooouo..........',
    '......onnnnnnoblbbbbono.........',
    '.......onnnnoblnnnnnnoo.........',
    '.......onnnnobnnnnnnnnbo........',
    '........onnnonnnnnnnnnnno.......',
    '........ounnnonnnnnnnnnno.......',
    '........onnnnonnnnnnnnnnno......',
    '.........onnnnonnnnnnnnnno......',
    '..........onnnnoonnnnnnnno......',
    '...........onnnnnonnnnnnono.....',
    '............onnnnnoooooonnno....',
    '.............oonnnnnnnnunnnno...',
    '..............odooodooonnnnnno..',
    '..............odo.odo..onnnnnno.',
    '..............odo.odo..onnnnnnno',
    '.............oodooodo...onnnnno.',
    '............oddddddddo..onnnoo..',
    '.............oooooooo...onoo....',
    '.........................o......',
    '................................',
  ],
);

export const leech = sprite(
  'sym_leech',
  {
    o: 'ink',
    l: 'slate',
    g: 'sage',
    c: 'charcoal',
    s: 'celadon',
    i: 'ice',
    b: 'blood',
    B: 'maroon',
    r: 'copper',
  },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '.............ooooo..............',
    '...........oogggggoo............',
    '..........oggcllcllgo...........',
    '.........ogiscrlcrllco..........',
    '........ogclllclcllsllo.........',
    '.......oglrcllclclclrllo........',
    '......ogslllclclclclliclo.......',
    '.....ogrrccccccclclllcslco......',
    '.....ogrccccccccclllcllrcco.....',
    '....ogccccccoooocclclllcllo.....',
    '....ogccccoo....occlllclllco....',
    '...ogcccco.......occlcllllcco...',
    '..occccco.........occlllllcco...',
    '..obBcco...........occlllccco...',
    '...oBco.............ooooooooo...',
    '....oo.............ollllllllco..',
    '...................occccccccco..',
    '....................oocccccoo...',
    '......................ooooo.....',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
  ],
);

// ---------------------------------------------------------------------------
// Rotten fruit
// ---------------------------------------------------------------------------

/** Fresh color → rotten color. Keys not listed keep their color. */
const ROT_REMAP: Partial<Record<PaletteKey, PaletteKey>> = {
  // reds (cherry, melon flesh)
  red: 'brick',
  blood: 'maroon',
  vermilion: 'copper',
  coral: 'bronze',
  cream: 'khaki',
  // yellows (lemon)
  lemon: 'lime',
  lime: 'moss',
  moss: 'olive',
  // purples (plum, grape)
  pinkLilac: 'sand',
  lavender: 'mauveGrey',
  violet: 'plumGrey',
  purple: 'aubergine',
  aubergine: 'night',
  // greens & stems
  leaf: 'moss',
  clover: 'olive',
  forest: 'mud',
  pine: 'mud',
  brick: 'mud',
  // highlights lose their shine
  white: 'khaki',
};

/** Stamp chars; digits never appear in the base fruit sprites. */
const STAMP_COLORS: Record<string, PaletteKey> = {
  '1': 'paper', // mold fuzz core
  '2': 'khaki', // mold fuzz
  '3': 'celadon', // mold edge
  '4': 'mud', // rot bruise core
  '5': 'olive', // rot bruise edge
};

type SpotKind = 'mold' | 'rot';
/** [x, y, kind] — spot center in sprite pixels. */
type Spot = readonly [number, number, SpotKind];

// Offsets [dx, dy, char] for each stamp shape.
const STAMPS: Record<SpotKind, readonly (readonly [number, number, string])[]> = {
  mold: [
    [0, 0, '1'],
    [-1, 0, '2'],
    [1, 0, '2'],
    [0, -1, '2'],
    [0, 1, '2'],
    [1, 1, '3'],
    [-1, 1, '3'],
    [1, -1, '3'],
  ],
  rot: [
    [0, 0, '4'],
    [1, 0, '4'],
    [0, 1, '4'],
    [1, 1, '4'],
    [-1, 0, '5'],
    [2, 1, '5'],
    [0, -1, '5'],
    [1, 2, '5'],
  ],
};

/** A fly buzzing off the rotten fruit: it also gives rot its own silhouette (colorblind-safe, docs/04 §24). */
const FLY = ['.6.6.', '6o6o6', '.ooo.', '..o..'];

function placeFly(grid: string[][]): void {
  const h = grid.length;
  const w = grid[0]!.length;
  const free = (x0: number, y0: number) => {
    for (let y = y0 - 1; y <= y0 + FLY.length; y++)
      for (let x = x0 - 1; x <= x0 + FLY[0]!.length; x++) {
        const c = grid[y]?.[x];
        if (c !== undefined && c !== '.' && c !== ' ') return false;
      }
    return y0 >= 0 && x0 >= 0 && y0 + FLY.length <= h && x0 + FLY[0]!.length <= w;
  };
  const spots = [
    [w - 6, 1],
    [1, 1],
    [w - 6, h - 5],
    [1, h - 5],
  ];
  const at = spots.find(([x, y]) => free(x!, y!));
  if (!at) return;
  FLY.forEach((row, dy) => [...row].forEach((ch, dx) => ch !== '.' && (grid[at[1]! + dy]![at[0]! + dx] = ch)));
}

export function rotten(base: SpriteDef, id: string, spots: readonly Spot[]): SpriteDef {
  const colors: Record<string, PaletteKey> = { ...STAMP_COLORS, '6': 'lilacGrey' };
  for (const [ch, key] of Object.entries(base.colors)) colors[ch] = ROT_REMAP[key] ?? key;
  const frames = base.frames.map((frame) => {
    const grid = frame.map((row) => row.split(''));
    for (const [sx, sy, kind] of spots)
      for (const [dx, dy, ch] of STAMPS[kind]) {
        const row = grid[sy + dy];
        const cur = row?.[sx + dx];
        // only on fruit pixels: never on transparency or the ink outline
        if (!row || cur === undefined || cur === '.' || cur === ' ' || cur === 'o') continue;
        row[sx + dx] = ch;
      }
    placeFly(grid);
    return grid.map((r) => r.join(''));
  });
  return { id, w: base.w, h: base.h, colors, frames };
}

export const rottenCherry = rotten(cherry, 'sym_rotten_cherry', [
  [7, 21, 'mold'],
  [11, 24, 'rot'],
  [22, 23, 'rot'],
  [24, 20, 'mold'],
]);
export const rottenLemon = rotten(lemon, 'sym_rotten_lemon', [
  [9, 17, 'rot'],
  [18, 12, 'mold'],
  [21, 19, 'rot'],
  [14, 21, 'mold'],
  [12, 11, 'rot'],
]);
export const rottenPlum = rotten(plum, 'sym_rotten_plum', [
  [10, 16, 'mold'],
  [19, 13, 'rot'],
  [21, 22, 'mold'],
  [13, 23, 'rot'],
]);
export const rottenGrape = rotten(grape, 'sym_rotten_grape', [
  [8, 9, 'mold'],
  [19, 14, 'rot'],
  [9, 20, 'rot'],
  [16, 24, 'mold'],
]);
export const rottenMelon = rotten(melon, 'sym_rotten_melon', [
  [9, 11, 'rot'],
  [15, 10, 'mold'],
  [20, 14, 'rot'],
  [13, 16, 'mold'],
]);

export const SYMBOL_SPRITES_D: SpriteDef[] = [
  maggot,
  fly,
  mushroom,
  rat,
  cat,
  worm,
  spider,
  crow,
  leech,
  rottenCherry,
  rottenLemon,
  rottenPlum,
  rottenGrape,
  rottenMelon,
];
